/**
 * Access denied screen for authentication-level access denied errors
 * Shows when user doesn't have access to the application at the Azure AD level
 */

import React from 'react';
import { 
  Title1,
  Subtitle1,
  Body1,
  Caption1,
  Button,
  tokens,
  makeStyles
} from '@fluentui/react-components';
import { 
  ShieldError24Regular,
  Person24Regular,
  ContactCard24Regular,
  Warning24Regular,
  ErrorCircle24Regular
} from '@fluentui/react-icons';
import { useAuth } from '../auth/AuthProvider';

const useStyles = makeStyles({
  accessDeniedContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    padding: tokens.spacingVerticalXXL,
  },
  accessDeniedCard: {
    maxWidth: '600px',
    width: '100%',
    textAlign: 'center',
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: tokens.spacingVerticalL,
  },
  errorIcon: {
    fontSize: '64px',
    color: tokens.colorPaletteRedForeground1,
  },
  title: {
    marginBottom: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightHero700,
    display: 'block',
    width: '100%',
    textAlign: 'center',
  },
  subtitle: {
    marginBottom: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase400,
    wordWrap: 'break-word',
  },
  detailsSection: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalL,
    textAlign: 'left',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
    '&:last-child': {
      marginBottom: '0',
    },
  },
  detailIcon: {
    color: tokens.colorNeutralForeground2,
    marginTop: '2px',
    flexShrink: 0,
  },
  detailLabel: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    minWidth: '120px',
    flexShrink: 0,
  },
  detailValue: {
    color: tokens.colorNeutralForeground2,
    wordBreak: 'break-all',
  },
  errorValue: {
    color: tokens.colorPaletteRedForeground1,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
  },
  contactMessage: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalL,
  },
  actionButtons: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    justifyContent: 'center',
    marginTop: tokens.spacingVerticalM,
  },
});

interface AccessDeniedScreenProps {
  error?: string;
  userEmail?: string;
  applicationName?: string;
}

export const AccessDeniedScreen: React.FC<AccessDeniedScreenProps> = ({
  error,
  userEmail,
  applicationName = 'Medical Management AI'
}) => {
  const classes = useStyles();
  const { login } = useAuth();

  // Parse error for more specific information
  const parseErrorDetails = (errorMsg?: string) => {
    if (!errorMsg) return null;

    const details: any = {};
    
    // Extract application name and ID
    const appMatch = errorMsg.match(/application ([^']+) \('([^']+)'\)/);
    if (appMatch) {
      details.applicationName = appMatch[1];
      details.applicationId = appMatch[2];
    }

    // Extract user email
    const userMatch = errorMsg.match(/user '([^']+)'/);
    if (userMatch) {
      details.userEmail = userMatch[1];
    }

    // Extract error code
    const codeMatch = errorMsg.match(/(AADSTS\d+)/);
    if (codeMatch) {
      details.errorCode = codeMatch[1];
    }

    return details;
  };

  const errorDetails = parseErrorDetails(error);
  const displayUserEmail = userEmail || errorDetails?.userEmail || 'Unknown';
  const displayAppName = applicationName || errorDetails?.applicationName || 'Medical Management AI';

  const handleRetryLogin = async () => {
    try {
      await login();
    } catch (error) {
      console.error('Retry login failed:', error);
    }
  };

  return (
    <div className={classes.accessDeniedContainer}>
      <div className={classes.accessDeniedCard}>
        {/* Error Icon */}
        <div className={classes.iconContainer}>
          <ShieldError24Regular className={classes.errorIcon} />
        </div>

        {/* Main Title */}
        <Title1 className={classes.title}>
          Access Denied
        </Title1>

        {/* Subtitle */}
        <Subtitle1 className={classes.subtitle}>
          You don't have permission to access {displayAppName}. Your administrator has configured this application to only allow assigned users.
        </Subtitle1>

        {/* Error Details */}
        <div className={classes.detailsSection}>
          <div className={classes.detailRow}>
            <Person24Regular className={classes.detailIcon} />
            <span className={classes.detailLabel}>User Account:</span>
            <Caption1 className={classes.detailValue}>
              {displayUserEmail}
            </Caption1>
          </div>
          
          {errorDetails?.applicationId && (
            <div className={classes.detailRow}>
              <Warning24Regular className={classes.detailIcon} />
              <span className={classes.detailLabel}>Application ID:</span>
              <Caption1 className={classes.detailValue}>
                {errorDetails.applicationId}
              </Caption1>
            </div>
          )}

          {errorDetails?.errorCode && (
            <div className={classes.detailRow}>
              <ErrorCircle24Regular className={classes.detailIcon} />
              <span className={classes.detailLabel}>Error Code:</span>
              <Caption1 className={classes.errorValue}>
                {errorDetails.errorCode}
              </Caption1>
            </div>
          )}
        </div>

        {/* Contact Message */}
        <div className={classes.contactMessage}>
          <div className={classes.detailRow}>
            <ContactCard24Regular className={classes.detailIcon} />
            <Body1>
              <strong>To request access:</strong><br />
              Contact your administrator to be assigned access to this application. 
              You need to be either directly assigned to the application or be a member 
              of a group that has been granted access.
            </Body1>
          </div>
        </div>

        {/* Action Buttons */}
        <div className={classes.actionButtons}>
          <Button 
            appearance="secondary"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </Button>
          <Button 
            appearance="primary"
            onClick={handleRetryLogin}
          >
            Try Again
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccessDeniedScreen;
